import React, { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@assets/icons/search.svg';
import type { SortValue } from '@components';
import { BaseInput } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { buildEditOrViewColumns } from '@routes/sidebar/admin-center/___shared___/buildEditOrViewColumns.tsx';
import { showNoSetValue } from '@routes/sidebar/admin-center/___shared___/components/tables/helpers.tsx';
import { CommonRegularTable } from '@routes/sidebar/admin-center/___shared___/components/tables/regular';
import { SearchInputWrapper } from '@routes/sidebar/admin-center/___shared___/styled.ts';
import {
    addSortableColumn,
    addValidationRulesToColumns,
    createOnTableChange,
    mapFieldsToColumn,
} from '@routes/sidebar/admin-center/___shared___/utils.ts';
import { buildSortRequest } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/utils.ts';
import { TableNoValidationMSGWrapper } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/styled.ts';
import type { ReferencesColumn } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import { getCertificatePdf } from '@routes/system/certificate/model/certificate.slice.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import type { ColumnsType, TableProps } from 'antd/es/table';
import startCase from 'lodash/startCase';
import uniq from 'lodash/uniq';

import {
    getColumnsFiltersValues,
    getLearningResults,
    updateLearningRequestData,
} from '../../../model/results.slice.ts';
import { MY_LEARNING_COLUMNS_VIEW } from './columns.ts';
import {
    MY_LEARNING_COLUMN_FILTER_REQUEST_MAP,
    MY_LEARNING_COLUMN_SORT_MAP,
    MY_LEARNING_DATA_INDEX,
} from './constants.ts';
import type { CertificateColumnData, MyLearningRow, PathwayColumnData } from './types.ts';
import {
    CertificateCell,
    CompletionMethodCell,
    formatAttempt,
    mapCompletionMethodLabel,
    mapTableFiltersToRequestFilters,
    mapUserLearningToRow,
    PathwayCell,
    StatusCell,
} from './utils.tsx';

const MyLearningTable: React.FC = () => {
    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const myLearningResults = useAppSelector((state) => state.results.myLearningResults);
    const requestData = useAppSelector((state) => state.results.myLearningRequestData);
    const filterValues = useAppSelector((state) => state.results.myColumnsFiltersValues);
    const userInfo = useAppSelector((state) => state.user.userInfo);
    const { preferredDateFormat: dateFormat } = getUserPreferencesOrDefaults(userInfo);

    const [sort, setSort] = useState<SortValue>({
        columnId: MY_LEARNING_DATA_INDEX.DATE,
        order: 'desc',
    });
    const [tableFilters, setTableFilters] = useState<Record<string, string[]> | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const PATHWAY = MY_LEARNING_DATA_INDEX.PATHWAY;
    const DATE = MY_LEARNING_DATA_INDEX.DATE;
    const RANK = MY_LEARNING_DATA_INDEX.RANK;
    const LOCATION = MY_LEARNING_DATA_INDEX.LOCATION;
    const SCORE = MY_LEARNING_DATA_INDEX.SCORE;
    const ATTEMPT = MY_LEARNING_DATA_INDEX.ATTEMPT;
    const STATUS = MY_LEARNING_DATA_INDEX.STATUS;
    const METHOD = MY_LEARNING_DATA_INDEX.METHOD;
    const CERTIFICATE = MY_LEARNING_DATA_INDEX.CERTIFICATE;

    useEffect(() => {
        void dispatch(getLearningResults(requestData));
    }, [dispatch, requestData]);

    useEffect(() => {
        void dispatch(getColumnsFiltersValues());
    }, [dispatch]);

    const rows: MyLearningRow[] = useMemo(
        () => myLearningResults.data.map(mapUserLearningToRow),
        [myLearningResults.data],
    );

    const hasActiveFilters = Boolean(
        (requestData.textSearch?.trim() ?? '') !== '' ||
        (requestData.filters && Object.keys(requestData.filters).length > 0),
    );

    const viewCols = useMemo<ReferencesColumn[]>(() => {
        const base = MY_LEARNING_COLUMNS_VIEW.map((field) => mapFieldsToColumn(field, t));
        return addValidationRulesToColumns(base, {});
    }, [t]);

    const baseColumns = useMemo(() => buildEditOrViewColumns<MyLearningRow>(viewCols, () => false), [viewCols]);

    const filterOptionsByColumn = useMemo(
        () => ({
            [RANK]: filterValues.ranks.map((rank) => ({ text: rank, value: rank })),
            [LOCATION]: filterValues.locations.map((location) => ({ text: location, value: location })),
            [STATUS]: filterValues.statuses.map((status) => ({
                text: startCase(String(status)),
                value: String(status),
            })),
            [METHOD]: filterValues.methods.map((method) => ({
                text: mapCompletionMethodLabel(method),
                value: method,
            })),
        }),
        [filterValues],
    );

    const columns: ColumnsType<MyLearningRow> = useMemo(() => {
        return baseColumns.map((col) => {
            const columnKey = String(col.key ?? '');
            let nextCol: any = col;

            if (columnKey === PATHWAY) {
                const titleOptions = uniq(rows.map((row) => row.title.title)).map((title) => ({
                    text: title,
                    value: title,
                }));
                nextCol = {
                    ...col,
                    filters: titleOptions,
                    filteredValue: tableFilters?.[columnKey] ?? null,
                    filterSearch: true,
                    render: (val: PathwayColumnData) => <PathwayCell {...val} />,
                };
            }

            if (columnKey === DATE) {
                nextCol = {
                    ...col,
                    render: (val: string) => formatDateBasedOnPreferences(val, dateFormat),
                };
            }

            if (columnKey === SCORE) {
                nextCol = {
                    ...col,
                    render: (val: number | null) => showNoSetValue(val),
                };
            }

            if (columnKey === ATTEMPT) {
                nextCol = {
                    ...col,
                    render: (val: { attempt: number | null; maxAttempts: number | null }) =>
                        formatAttempt(val.attempt, val.maxAttempts),
                };
            }

            if (columnKey === STATUS) {
                nextCol = {
                    ...col,
                    render: (val: string) => <StatusCell status={val} />,
                };
            }

            if (columnKey === METHOD) {
                nextCol = {
                    ...col,
                    render: (val: MyLearningRow['completionMethod']) => <CompletionMethodCell method={val} />,
                };
            }

            if (columnKey === CERTIFICATE) {
                nextCol = {
                    ...col,
                    render: (val: CertificateColumnData) => (
                        <CertificateCell
                            {...val}
                            onOpen={() => {
                                void dispatch(getCertificatePdf(val.resultId));
                            }}
                        />
                    ),
                };
            }

            const options = filterOptionsByColumn[columnKey as keyof typeof filterOptionsByColumn];
            if (Array.isArray(nextCol.filters) && options && columnKey !== PATHWAY) {
                nextCol = {
                    ...nextCol,
                    filters: options.map((option) => ({ text: option.text, value: option.value })),
                    filteredValue: tableFilters?.[columnKey] ?? null,
                    filterSearch: true,
                };
            }

            return addSortableColumn(nextCol, sort, MY_LEARNING_COLUMN_SORT_MAP);
        });
    }, [baseColumns, filterOptionsByColumn, tableFilters, sort, dateFormat, rows, dispatch]);

    const handleSearch = (value: string) => {
        setSearchValue(value);
        dispatch(
            updateLearningRequestData({
                ...requestData,
                textSearch: value,
                pageNumber: 1,
            }),
        );
    };

    const handleTableChange: TableProps<MyLearningRow>['onChange'] = createOnTableChange<MyLearningRow>({
        setSort,
        getSort: () => sort,
        getSearchValue: () => searchValue,
        setSearchValue,
        onFilterChange: setTableFilters,
        debouncedSearch: (value, nextSort, filters) => {
            const { sortBy, sortOrder } = buildSortRequest(nextSort, MY_LEARNING_COLUMN_SORT_MAP);
            dispatch(
                updateLearningRequestData({
                    ...requestData,
                    textSearch: value,
                    sortBy,
                    sortOrder,
                    filters: mapTableFiltersToRequestFilters(filters ?? null, MY_LEARNING_COLUMN_FILTER_REQUEST_MAP),
                    pageNumber: 1,
                }),
            );
        },
    });

    const totalItems = myLearningResults.pagination.totalCount;
    const pageNumber = requestData.pageNumber ?? 1;
    const pageSize = requestData.pageSize ?? 10;

    return (
        <>
            <SearchInputWrapper>
                <BaseInput
                    value={searchValue}
                    allowClear
                    placeholder={t`Search`}
                    prefixIcon={<SearchIcon />}
                    onChange={(event) => handleSearch(event.target.value)}
                />
            </SearchInputWrapper>
            <TableNoValidationMSGWrapper>
                <CommonRegularTable<MyLearningRow>
                    tableLayout="fixed"
                    rows={rows}
                    columns={columns}
                    emptyTitle={t`No learning activities`}
                    emptyDescription={t`You don't have any learning activities yet`}
                    hasActiveFilters={hasActiveFilters}
                    pagination={{
                        pageNumber,
                        pageSize,
                        totalItems,
                    }}
                    onPaginationChange={(nextPageNumber, nextPageSize) => {
                        dispatch(
                            updateLearningRequestData({
                                ...requestData,
                                pageNumber: nextPageNumber,
                                pageSize: nextPageSize,
                            }),
                        );
                    }}
                    onTableChange={handleTableChange}
                />
            </TableNoValidationMSGWrapper>
        </>
    );
};

export default MyLearningTable;
